import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  getPurchaseOrders,
  createPurchaseOrder,
  updatePurchaseOrder,
  deletePurchaseOrder,
  getPOPayments,
  createPOPayment,
  updatePaymentStatus,
} from "../../feature/purchaseOrder/purchaseOrderThunks";

import {
  selectPurchaseOrders,
  selectPurchaseOrderLoading,
  selectCreatePurchaseOrderLoading,
  selectCreatePurchaseOrderSuccess,
  selectUpdatePurchaseOrderLoading,
  selectUpdatePurchaseOrderSuccess,
  selectPurchaseOrderError,
  selectDeletePurchaseOrderLoading,
  selectPOPayments,
  selectPOPaymentsLoading,
} from "../../feature/purchaseOrder/purchaseOrderSelector";
import { getDistributors } from "../../feature/distributors/distributorThunks";
selectDistributors;
// TODO: Import your product and distributor selectors
// import { selectProducts } from "../../feature/product/productSelector";
// import { selectDistributors } from "../../feature/distributor/distributorSelector";
// import { getProducts } from "../../feature/product/productThunks";
// import { getDistributors } from "../../feature/distributor/distributorThunks";

import {
  Box,
  Paper,
  Stack,
  Typography,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Pagination,
  CircularProgress,
  Grid,
  Divider,
  Card,
  CardContent,
  InputAdornment,
  Button,
  Alert,
  Autocomplete,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PaymentsIcon from "@mui/icons-material/Payments";
import ShieldIcon from "@mui/icons-material/Shield";

import CommonButton from "../../components/commonComponents/CommonButton";
import CommonLabel from "../../components/commonComponents/CommonLabel";
import CommonToast from "../../components/commonComponents/Toster";
import CommonSearchField from "../../components/commonComponents/CommonSearchField";
import CommonSelectField from "../../components/commonComponents/CommonSelectField";

import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";

import {
  createShipment,
  getShipments,
  updateShipment,
} from "../../feature/shipment/shipmentFormThunks";
import {
  selectCreateShipmentFormLoading,
  selectUpdateShipmentFormLoading,
} from "../../feature/shipment/shipmentFormSelector";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { selectDistributors } from "../../feature/distributors/distributorSelector";

// ─── Shared style tokens ────────────────────────────────────────────────────
const CARD_RADIUS = 3;
const SHADOW = "0 2px 12px 0 rgba(30,41,59,.08)";
// const HEADER_BG = "linear-gradient(135deg,#1e3a5f 0%,#2563eb 100%)";
const HEADER_BG = "linear-gradient(90deg, #D20000 0%, #8B0000 100%)";

const tableHeadSx = {
  "& .MuiTableCell-head": {
    bgcolor: "#f1f5f9",
    color: "#334155",
    fontWeight: 700,
    fontSize: "0.78rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderBottom: "2px solid #e2e8f0",
    py: 1.5,
  },
};

const tableRowHoverSx = {
  transition: "background 0.15s",
  "&:hover": { bgcolor: "#f8faff" },
  "& .MuiTableCell-root": {
    borderBottom: "1px solid #f1f5f9",
    py: 1.25,
  },
};

const sectionPaper = {
  borderRadius: CARD_RADIUS,
  boxShadow: SHADOW,
  border: "1px solid #e2e8f0",
  overflow: "hidden",
};

// ─── Status colour map ──────────────────────────────────────────────────────
const STATUS_PALETTE = {
  DRAFT: { bg: "#64748b", light: "#f1f5f9", text: "#475569" },
  SUBMITTED: { bg: "#f59e0b", light: "#fffbeb", text: "#92400e" },
  APPROVED: { bg: "#10b981", light: "#ecfdf5", text: "#065f46" },
  REJECTED: { bg: "#ef4444", light: "#fef2f2", text: "#991b1b" },
  PARTIALLY_APPROVED: { bg: "#06b6d4", light: "#ecfeff", text: "#164e63" },
  CANCELLED: { bg: "#94a3b8", light: "#f8fafc", text: "#475569" },
  PICKED: { bg: "#f97316", light: "#fff7ed", text: "#9a3412" },
  PACKED: { bg: "#3b82f6", light: "#eff6ff", text: "#1e40af" },
  DELIVERED: { bg: "#7c3aed", light: "#f5f3ff", text: "#4c1d95" },
};

const StatusBadge = ({ status }) => {
  const p = STATUS_PALETTE[status] || STATUS_PALETTE.DRAFT;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 1.5,
        py: 0.4,
        borderRadius: "999px",
        bgcolor: p.light,
        color: p.text,
        fontWeight: 700,
        fontSize: "0.72rem",
        letterSpacing: "0.04em",
        border: `1.5px solid ${p.bg}33`,
      }}
    >
      {status}
    </Box>
  );
};

// ────────────────────────────────────────────────────────────────────────────
const ShipmentFormDialog = ({
  open,
  onClose,
  poId,
  dispatch,
  createLoading,
  updateLoading,
}) => {
  const SHIPMENT_STATUS_OPTIONS = [
    { value: "created", label: "Created" },
    { value: "picked", label: "Picked By Courier" },
    { value: "in_transit", label: "In Transit" },
    { value: "out_for_delivery", label: "Out For Delivery" },
    { value: "delivered", label: "Delivered" },
    { value: "failed", label: "Failed" },
  ];

  const emptyForm = {
    tracking_number: "",
    shipment_status: "created",
    shipped_at: "",
    estimated_delivery: "",
    name: "",
    contact_number: "",
    email: "",
    service_type: "",
    image: null,
  };

  const [shipmentForm, setShipmentForm] = useState(emptyForm);
  const [existingShipmentId, setExistingShipmentId] = useState(null);
  const [fetchingShipment, setFetchingShipment] = useState(false);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  const BASE_URL = "https://apidata.hogonnindia.com";

  useEffect(() => {
    if (!open || !poId) return;
    const fetchExisting = async () => {
      setFetchingShipment(true);
      setExistingShipmentId(null);
      setExistingImageUrl(null);
      setImagePreview(null);
      setErrors({});
      setShipmentForm(emptyForm);
      try {
        const res = await fetch(
          `https://apidata.hogonnindia.com/shipment/?order_id=${poId}`,
        );
        const data = await res.json();
        const shipment = Array.isArray(data)
          ? data.find((s) => s.order_id === poId) || data[0]
          : data?.data?.find((s) => s.order_id === poId) || data?.data?.[0];

        if (shipment) {
          setExistingShipmentId(shipment.id);
          setExistingImageUrl(shipment.image || null);
          setShipmentForm({
            tracking_number: shipment.tracking_number || "",
            shipment_status: shipment.shipment_status || "created",
            shipped_at: shipment.shipped_at
              ? shipment.shipped_at.slice(0, 16)
              : "",
            estimated_delivery: shipment.estimated_delivery || "",
            name: shipment.name || "",
            contact_number: shipment.contact_number || "",
            email: shipment.email || "",
            service_type: shipment.service_type || "",
            image: null,
          });
        }
      } catch (err) {
        console.error("Failed to fetch shipment", err);
      } finally {
        setFetchingShipment(false);
      }
    };
    fetchExisting();
  }, [open, poId]);

  // cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const handleChange = (field, value) => {
    setShipmentForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleImageChange = (file) => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    if (file) {
      setImagePreview(URL.createObjectURL(file));
      handleChange("image", file);
    } else {
      setImagePreview(null);
      handleChange("image", null);
    }
  };

  const buildFormData = () => {
    const formData = new FormData();
    formData.append("order_id", poId);
    formData.append("tracking_number", shipmentForm.tracking_number);
    formData.append("shipment_status", shipmentForm.shipment_status);
    if (shipmentForm.shipped_at)
      formData.append("shipped_at", shipmentForm.shipped_at);
    if (shipmentForm.estimated_delivery)
      formData.append("estimated_delivery", shipmentForm.estimated_delivery);
    formData.append("name", shipmentForm.name);
    formData.append("contact_number", shipmentForm.contact_number);
    formData.append("email", shipmentForm.email);
    formData.append("service_type", shipmentForm.service_type);
    if (shipmentForm.image) formData.append("image", shipmentForm.image);
    return formData;
  };

  const handleSubmit = () => {
    const newErrors = {};
    if (
      shipmentForm.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shipmentForm.email)
    ) {
      newErrors.email = "Please enter a valid email address";
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    const formData = buildFormData();
    const isEdit = !!existingShipmentId;
    const action = isEdit
      ? dispatch(updateShipment({ id: existingShipmentId, data: formData }))
      : dispatch(createShipment(formData));

    action
      .unwrap()
      .then(() => {
        CommonToast(
          isEdit
            ? "Shipment updated successfully"
            : "Shipment created successfully",
          "success",
        );
        onClose();
      })
      .catch(() =>
        CommonToast(
          isEdit ? "Failed to update shipment" : "Failed to create shipment",
          "error",
        ),
      );
  };

  const isEdit = !!existingShipmentId;
  const isLoading = isEdit ? updateLoading : createLoading;

  // active preview = new file preview OR existing URL
  const activeImageSrc = imagePreview
    ? imagePreview
    : existingImageUrl
      ? `${BASE_URL}${existingImageUrl}`
      : null;

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      height: 44,
      fontSize: "0.875rem",
      bgcolor: "#fff",
      "& fieldset": { borderColor: "#e2e8f0" },
      "&:hover fieldset": { borderColor: "#94a3b8" },
      "&.Mui-focused fieldset": { borderColor: "#D20000" },
    },
    "& .MuiInputLabel-root": { fontSize: "0.875rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#D20000" },
  };

  const sectionLabel = (text) => (
    <Typography
      variant="caption"
      fontWeight={700}
      color="#94a3b8"
      letterSpacing="0.08em"
      display="block"
      mb={1.5}
      sx={{ textTransform: "uppercase", fontSize: "0.68rem" }}
    >
      {text}
    </Typography>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 25px 60px rgba(0,0,0,0.18)",
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          background: HEADER_BG,
          px: 3.5,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              bgcolor: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LocalShippingIcon sx={{ color: "white", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color="white"
              lineHeight={1.2}
            >
              {isEdit ? "Edit Shipment" : "Create Shipment"}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.65)" }}
            >
              Purchase Order ID: {poId}
            </Typography>
          </Box>
        </Box>
        {isEdit && (
          <Box
            sx={{
              bgcolor: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "999px",
              px: 2,
              py: 0.5,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={700}
              color="white"
              letterSpacing="0.06em"
            >
              EDIT MODE
            </Typography>
          </Box>
        )}
      </Box>

      <DialogContent sx={{ p: 0, bgcolor: "#f8fafc" }}>
        {fetchingShipment ? (
          <Stack alignItems="center" py={8} spacing={2}>
            <CircularProgress
              size={32}
              thickness={3.5}
              sx={{ color: "#D20000" }}
            />
            <Typography variant="body2" color="text.secondary">
              Loading shipment data…
            </Typography>
          </Stack>
        ) : (
          <Box sx={{ display: "flex", gap: 0, minHeight: 520 }}>
            {/* ── LEFT: Image Panel ── */}
            <Box
              sx={{
                width: 220,
                minWidth: 220,
                bgcolor: "#fff",
                borderRight: "1px solid #e2e8f0",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 3,
                gap: 2,
              }}
            >
              {sectionLabel("Courier Image")}

              {/* Image preview box */}
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  width: 156,
                  height: 156,
                  borderRadius: "14px",
                  border: `2px dashed ${activeImageSrc ? "#D20000" : "#cbd5e1"}`,
                  overflow: "hidden",
                  cursor: "pointer",
                  bgcolor: "#f8fafc",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "#D20000",
                    bgcolor: "#fff5f5",
                  },
                  position: "relative",
                }}
              >
                {activeImageSrc ? (
                  <>
                    <Box
                      component="img"
                      src={activeImageSrc}
                      alt="courier"
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    {/* overlay on hover */}
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        bgcolor: "rgba(210,0,0,0.55)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: 0,
                        transition: "opacity 0.2s",
                        "&:hover": { opacity: 1 },
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="white"
                      >
                        Click to change
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <>
                    <LocalShippingIcon
                      sx={{ fontSize: 36, color: "#cbd5e1", mb: 1 }}
                    />
                    <Typography
                      variant="caption"
                      color="#94a3b8"
                      textAlign="center"
                      px={1}
                    >
                      Click to upload image
                    </Typography>
                  </>
                )}
              </Box>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => handleImageChange(e.target.files[0] || null)}
              />

              {/* File info */}
              {shipmentForm.image ? (
                <Box textAlign="center">
                  <Typography
                    variant="caption"
                    color="#10b981"
                    fontWeight={600}
                    display="block"
                  >
                    ✓ New image selected
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      maxWidth: 160,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {shipmentForm.image.name}
                  </Typography>
                </Box>
              ) : isEdit && existingImageUrl ? (
                <Typography
                  variant="caption"
                  color="#94a3b8"
                  textAlign="center"
                >
                  Showing current image.
                  <br />
                  Click to replace.
                </Typography>
              ) : null}

              {/* Remove button */}
              {activeImageSrc && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    handleImageChange(null);
                    setExistingImageUrl(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "0.75rem",
                    py: 0.5,
                    mt: "auto",
                  }}
                >
                  Remove Image
                </Button>
              )}
            </Box>

            {/* ── RIGHT: Form Fields ── */}
            <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
              {/* Row 1: Tracking Info */}
              {sectionLabel("Tracking Info")}
              <Grid container spacing={2} mb={2.5}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Tracking Number"
                    value={shipmentForm.tracking_number}
                    onChange={(e) =>
                      handleChange("tracking_number", e.target.value)
                    }
                    fullWidth
                    size="small"
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Shipment Status"
                    value={shipmentForm.shipment_status}
                    onChange={(e) =>
                      handleChange("shipment_status", e.target.value)
                    }
                    select
                    fullWidth
                    size="small"
                    sx={{
                      ...inputSx,
                      "& .MuiOutlinedInput-root": {
                        ...inputSx["& .MuiOutlinedInput-root"],
                        height: 44,
                      },
                    }}
                  >
                    {SHIPMENT_STATUS_OPTIONS.map((s) => (
                      <MenuItem
                        key={s.value}
                        value={s.value}
                        sx={{ fontSize: "0.875rem" }}
                      >
                        {s.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              {/* Row 2: Dates */}
              {sectionLabel("Schedule")}
              <Grid container spacing={2} mb={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Shipped At"
                    type="datetime-local"
                    value={shipmentForm.shipped_at}
                    onChange={(e) => handleChange("shipped_at", e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Estimated Delivery"
                    type="date"
                    value={shipmentForm.estimated_delivery}
                    onChange={(e) =>
                      handleChange("estimated_delivery", e.target.value)
                    }
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={inputSx}
                  />
                </Grid>
              </Grid>

              {/* Row 3: Courier Details */}
              {sectionLabel("Courier Details")}
              <Grid container spacing={2} mb={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Courier Name"
                    value={shipmentForm.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    fullWidth
                    size="small"
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Service Type"
                    value={shipmentForm.service_type}
                    onChange={(e) =>
                      handleChange("service_type", e.target.value)
                    }
                    fullWidth
                    size="small"
                    sx={inputSx}
                  />
                </Grid>
              </Grid>

              {/* Row 4: Contact */}
              {sectionLabel("Contact")}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Contact Number"
                    value={shipmentForm.contact_number}
                    onChange={(e) =>
                      handleChange("contact_number", e.target.value)
                    }
                    fullWidth
                    size="small"
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    value={shipmentForm.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    fullWidth
                    size="small"
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email}
                    sx={inputSx}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* ── Footer ── */}
      <Box
        sx={{
          px: 3,
          py: 2,
          bgcolor: "#fff",
          borderTop: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            height: 40,
            borderColor: "#e2e8f0",
            color: "#64748b",
            "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || fetchingShipment}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 700,
            px: 3.5,
            height: 40,
            background: HEADER_BG,
            boxShadow: "0 4px 14px rgba(210,0,0,0.3)",
            "&:hover": { boxShadow: "0 6px 20px rgba(210,0,0,0.4)" },
            "&.Mui-disabled": { opacity: 0.6 },
          }}
        >
          {isLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={14} sx={{ color: "white" }} />
              <span>{isEdit ? "Updating…" : "Creating…"}</span>
            </Stack>
          ) : isEdit ? (
            "Update Shipment"
          ) : (
            "Create Shipment"
          )}
        </Button>
      </Box>
    </Dialog>
  );
};

const PaymentsPanel = ({
  poPayments,
  paymentsLoading,
  BASE_URL,
  dispatch,
  getPOPayments,
  createPOPayment,
  updatePaymentStatus,
}) => {
  const PAYMENT_STATUS_OPTIONS = ["Pending", "Approved", "Rejected"];
  const poData = poPayments?.[0];

  const [showAddPaymentDialog, setShowAddPaymentDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    payment_date: "",
    image: null,
  });
  const [paymentErrors, setPaymentErrors] = useState({});

  const handlePaymentStatusChange = (paymentId, newStatus) => {
    dispatch(updatePaymentStatus({ id: paymentId, status: newStatus }))
      .unwrap()
      .then(() => {
        CommonToast("Payment status updated", "success");
        dispatch(getPOPayments(poData?.purchase_order));
      })
      .catch(() => CommonToast("Failed to update status", "error"));
  };

  const validatePaymentForm = () => {
    const errors = {};
    if (!paymentForm.amount) errors.amount = "Amount is required";
    if (!paymentForm.payment_date)
      errors.payment_date = "Payment date is required";
    return errors;
  };

  const handleAddPayment = () => {
    const errors = validatePaymentForm();
    if (Object.keys(errors).length > 0) {
      setPaymentErrors(errors);
      return;
    }

    const payload = {
      distributor: poData?.distributor || poData?.distributor_id,
      purchase_order: poData?.purchase_order || poData?.id,
      amount: Number(paymentForm.amount),
      payment_date: paymentForm.payment_date,
      image: paymentForm.image || "",
    };

    dispatch(createPOPayment(payload))
      .unwrap()
      .then(() => {
        CommonToast("Payment added successfully", "success");
        setShowAddPaymentDialog(false);
        setPaymentForm({ amount: "", payment_date: "", image: null });
        setPaymentErrors({});
        dispatch(getPOPayments(poData?.purchase_order));
      })
      .catch((error) => {
        CommonToast(error?.message || "Failed to add payment", "error");
      });
  };

  return (
    <Paper sx={{ ...sectionPaper, p: 0, mt: 3 }}>
      <Box
        sx={{
          background: HEADER_BG,
          px: 3,
          py: 2,
          borderRadius: "12px 12px 0 0",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <PaymentsIcon sx={{ color: "white", fontSize: 20 }} />
        <Box>
          <Typography variant="subtitle1" fontWeight={700} color="white">
            Payment Records
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
            {poData?.payments?.length || 0} payment
            {poData?.payments?.length !== 1 ? "s" : ""}
          </Typography>
        </Box>
        <Box sx={{ marginLeft: "auto" }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAddPaymentDialog(true)}
            sx={{
              textTransform: "none",
              bgcolor: "#fff",
              color: "#1e293b",
              borderRadius: 2,
              px: 2.5,
              py: 1,
              fontWeight: 700,
              boxShadow: "none",
              minWidth: 130,
            }}
          >
            Add Payment
          </Button>
        </Box>
      </Box>

      <Dialog
        open={showAddPaymentDialog}
        onClose={() => {
          setShowAddPaymentDialog(false);
          setPaymentErrors({});
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Add Payment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Amount"
              type="number"
              value={paymentForm.amount}
              onChange={(e) => {
                setPaymentForm((prev) => ({
                  ...prev,
                  amount: e.target.value,
                }));
                setPaymentErrors((prev) => ({ ...prev, amount: "" }));
              }}
              error={Boolean(paymentErrors.amount)}
              helperText={paymentErrors.amount}
              fullWidth
            />
            <TextField
              label="Payment Date"
              type="date"
              value={paymentForm.payment_date}
              onChange={(e) => {
                setPaymentForm((prev) => ({
                  ...prev,
                  payment_date: e.target.value,
                }));
                setPaymentErrors((prev) => ({ ...prev, payment_date: "" }));
              }}
              InputLabelProps={{ shrink: true }}
              error={Boolean(paymentErrors.payment_date)}
              helperText={paymentErrors.payment_date}
              fullWidth
            />
            <TextField
              label="Payment Image"
              type="file"
              inputProps={{ accept: "image/*" }}
              onChange={(e) =>
                setPaymentForm((prev) => ({
                  ...prev,
                  image: e.target.files?.[0] || null,
                }))
              }
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={() => {
              setShowAddPaymentDialog(false);
              setPaymentErrors({});
            }}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddPayment}
            variant="contained"
            sx={{ textTransform: "none" }}
          >
            Add Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Summary strip */}
      {poData && (
        <Stack
          direction="row"
          spacing={2}
          sx={{ px: 3, pt: 2.5 }}
          flexWrap="wrap"
        >
          {[
            { label: "PO Number", value: poData.po_number },
            { label: "Distributor", value: poData.distributor_name },
            { label: "Total Payments", value: poData.payments?.length || 0 },
            {
              label: "Total Paid",
              value: `₹ ${(poData.payments || []).reduce((s, p) => s + Number(p.amount), 0).toFixed(2)}`,
            },
          ].map((item) => (
            <Box
              key={item.label}
              sx={{
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 2,
                px: 2,
                py: 1.5,
                minWidth: 130,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                letterSpacing="0.06em"
              >
                {item.label.toUpperCase()}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={700}
                color="#1e293b"
                mt={0.3}
              >
                {item.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}

      <Box sx={{ p: 3 }}>
        {paymentsLoading ? (
          <Stack alignItems="center" py={5} spacing={1.5}>
            <CircularProgress size={28} thickness={4} />
            <Typography variant="body2" color="text.secondary">
              Loading payments…
            </Typography>
          </Stack>
        ) : !poData || !poData.payments || poData.payments.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 5,
              bgcolor: "#f8fafc",
              borderRadius: 2,
              border: "1px dashed #cbd5e1",
            }}
          >
            <PaymentsIcon sx={{ fontSize: 36, color: "#cbd5e1", mb: 1 }} />
            <Typography color="text.secondary" fontSize="0.9rem">
              No payments recorded for this PO
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <Table size="small">
              <TableHead sx={tableHeadSx}>
                <TableRow>
                  {[
                    "#",
                    "Amount",
                    "Payment Date",
                    "Approved By",
                    "Status",
                    "Images",
                  ].map((h) => (
                    <TableCell key={h} align={h === "#" ? "left" : "center"}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {poData.payments.map((payment, idx) => {
                  const sc =
                    payment.status === "Approved"
                      ? { bg: "#ecfdf5", text: "#065f46", border: "#10b98133" }
                      : payment.status === "Rejected"
                        ? {
                            bg: "#fef2f2",
                            text: "#991b1b",
                            border: "#ef444433",
                          }
                        : {
                            bg: "#fffbeb",
                            text: "#92400e",
                            border: "#f59e0b33",
                          };

                  return (
                    <TableRow key={payment.id} sx={tableRowHoverSx}>
                      <TableCell>
                        <Chip
                          label={idx + 1}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.7rem",
                            bgcolor: "#f1f5f9",
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="#1d4ed8"
                        >
                          ₹ {Number(payment.amount).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="#475569">
                          {new Date(payment.payment_date).toLocaleDateString(
                            "en-IN",
                            { day: "2-digit", month: "short", year: "numeric" },
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="#1e293b"
                        >
                          {payment.admin_name || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Select
                          size="small"
                          value={payment.status}
                          onChange={(e) =>
                            handlePaymentStatusChange(
                              payment.id,
                              e.target.value,
                            )
                          }
                          sx={{
                            minWidth: 120,
                            height: 28,
                            borderRadius: "999px",
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            letterSpacing: "0.04em",
                            bgcolor: sc.bg,
                            color: sc.text,
                            "& .MuiSelect-select": { py: 0.5, pl: 2 },
                            "& fieldset": {
                              border: `1.5px solid ${sc.border}`,
                            },
                            "& svg": { color: sc.text },
                          }}
                        >
                          {PAYMENT_STATUS_OPTIONS.map((opt) => (
                            <MenuItem
                              key={opt}
                              value={opt}
                              sx={{ fontSize: "0.82rem", fontWeight: 600 }}
                            >
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                          flexWrap="wrap"
                        >
                          {payment.images?.length > 0 ? (
                            payment.images.map((img, i) => (
                              <Box
                                key={i}
                                component="a"
                                href={`${BASE_URL}${img}`}
                                target="_blank"
                                rel="noreferrer"
                                sx={{
                                  display: "block",
                                  width: 48,
                                  height: 48,
                                  borderRadius: 1.5,
                                  overflow: "hidden",
                                  border: "2px solid #e2e8f0",
                                  "&:hover": { borderColor: "#D20000" },
                                  transition: "border-color 0.15s",
                                }}
                              >
                                <Box
                                  component="img"
                                  src={`${BASE_URL}${img}`}
                                  alt={`img-${i + 1}`}
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </Box>
                            ))
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              —
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Paper>
  );
};

const PurchaseOrder = () => {
  const dispatch = useDispatch();

  const purchaseOrders = useSelector(selectPurchaseOrders);
  const loading = useSelector(selectPurchaseOrderLoading);
  const createLoading = useSelector(selectCreatePurchaseOrderLoading);
  const createSuccess = useSelector(selectCreatePurchaseOrderSuccess);
  const updateLoading = useSelector(selectUpdatePurchaseOrderLoading);
  const updateSuccess = useSelector(selectUpdatePurchaseOrderSuccess);
  const updateError = useSelector(selectPurchaseOrderError);
  const deleteLoading = useSelector(selectDeletePurchaseOrderLoading);

  // TODO: Replace these with your actual Redux selectors
  // const products = useSelector(selectProducts);
  const distributors = useSelector(selectDistributors);

  // MOCK DATA - Replace with actual API data
  const [products, setProducts] = useState([
    {
      product_id: 1,
      product_name: "Bike",
      unit_distributor_price: 50,
      mrp: 50,
    },
    {
      product_id: 2,
      product_name: "Cycle",
      unit_distributor_price: 30,
      mrp: 35,
    },
    {
      product_id: 3,
      product_name: "Helmet",
      unit_distributor_price: 20,
      mrp: 25,
    },
    {
      product_id: 4,
      product_name: "Lock",
      unit_distributor_price: 15,
      mrp: 18,
    },
  ]);

  // const [distributors, setDistributors] = useState([
  //   { distributor_id: 1, distributor_name: "ABC Distributors" },
  //   { distributor_id: 2, distributor_name: "XYZ Suppliers" },
  //   { distributor_id: 3, distributor_name: "Global Trading Co." },
  // ]);

  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const [isViewing, setIsViewing] = useState(false);
  const [viewPO, setViewPO] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");

  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [distributorFilter, setDistributorFilter] = useState("");
  const [form, setForm] = useState({
    po_number: "",
    distributor_id: "",
    company_id: "",
    product_items: [],
    remarks: "",
  });
  const [poStatus, setPoStatus] = useState("");

  const [productForm, setProductForm] = useState({
    product_id: "",
    product_name: "",
    quantity: 1,
    unit_distributor_price: 0,
    total_distributor_price: 0,
    mrp: 0,
  });

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedDistributor, setSelectedDistributor] = useState(null);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Shipment
  const shipmentCreateLoading = useSelector(selectCreateShipmentFormLoading);
  const shipmentUpdateLoading = useSelector(selectUpdateShipmentFormLoading); // add this
  const [shipmentDialogOpen, setShipmentDialogOpen] = useState(false);
  const [shipmentPOId, setShipmentPOId] = useState(null);

  const handleOpenShipmentForm = (po) => {
    setShipmentPOId(po.id);
    setShipmentDialogOpen(true);
  };

  // Invoice PDF download
  const handleDownloadInvoice = async (poId) => {
    try {
      const response = await fetch(
        `https://apidata.hogonnindia.com/purchase-orders/${poId}/invoice-pdf/`,
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice-PO-${poId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      CommonToast("Failed to download invoice", "error");
    }
  };

  // Barcode Sticker Bulk PDF Download
  // Barcode Sticker Bulk PDF Download
  // Barcode Sticker Bulk PDF Download
  const handleDownloadWarrantyCard = async (poNumber) => {
    try {
      const response = await fetch(
        `https://apidata.hogonnindia.com/warranty-card/bulk-pdf/?po_number=${poNumber}`,
      );

      const contentType = response.headers.get("content-type");

      // If it's JSON (error response)
      if (contentType && contentType.includes("application/json")) {
        const errorData = await response.json();

        if (!errorData.success) {
          CommonToast(
            errorData.message || "Failed to download Barcode Sticker",
            "error",
          );
          return;
        }
      }

      // If it's PDF (success)
      if (!response.ok) {
        CommonToast("Failed to download Barcode Sticker", "error");
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Warranty-Card-${poNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      CommonToast("Barcode Sticker downloaded successfully", "success");
    } catch (err) {
      console.error("Warranty PDF download error:", err);
      CommonToast("Failed to download Barcode Sticker", "error");
    }
  };
  const [errors, setErrors] = useState({});

  const hasStockError = form.product_items.some(
    (item) => item.quantity > item.stock_available,
  );

  const poPayments = useSelector(selectPOPayments);
  const paymentsLoading = useSelector(selectPOPaymentsLoading);

  const BASE_URL = "https://apidata.hogonnindia.com";

  const [isViewingPayments, setIsViewingPayments] = useState(false);
  const [viewPaymentPO, setViewPaymentPO] = useState(null);

  const handleViewPayments = (po) => {
    dispatch(getPOPayments(po.id));
    // setViewPaymentPO(po);
    // setIsViewingPayments(true);
  };

  useEffect(() => {
    dispatch(getDistributors());
  }, []);
  // ================= FETCH DATA =================
  // Reset page whenever search or filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter, distributorFilter]);

  // Fetch purchase orders (debounced) when filters or page change
  useEffect(() => {
    const delay = setTimeout(() => {
      const params = {};
      if (searchQuery) params.po_number = searchQuery;
      if (statusFilter) params.status = statusFilter;
      if (distributorFilter) params.distributor_id = distributorFilter;
      if (page) params.page = page;
      dispatch(getPurchaseOrders(params));
    }, 300);
    return () => clearTimeout(delay);
  }, [dispatch, searchQuery, statusFilter, distributorFilter, page]);

  useEffect(() => {
    if (updateError && updateError[0]) {
      const message = updateError[0];
      CommonToast(message, "info");
    }
  }, [updateError]);

  useEffect(() => {
    if (createSuccess || updateSuccess) {
      const params = {};
      if (searchQuery) params.po_number = searchQuery;
      if (statusFilter) params.status = statusFilter;
      if (distributorFilter) params.distributor_id = distributorFilter;
      if (page) params.page = page;
      dispatch(getPurchaseOrders(params));
      handleReset();
    }
  }, [createSuccess, updateSuccess, dispatch]);

  // ADD this new useEffect
  useEffect(() => {
    fetch("https://apidata.hogonnindia.com/company_profile/")
      .then((res) => res.json())
      .then((data) => {
        if (data?.success) setCompanies(data.data);
      })
      .catch((err) => console.error("Failed to fetch companies", err));
  }, []);

  // ================= PRODUCT CALCULATIONS =================
  const calculateProductTotals = (item) => {
    const quantity = Number(item.quantity) || 0;
    const unitDistPrice = Number(item.unit_distributor_price) || 0;
    const unitMRP = Number(item.mrp) || 0;
    return {
      ...item,
      quantity,
      unit_distributor_price: unitDistPrice,
      mrp: unitMRP,
      total_distributor_price: quantity * unitDistPrice,
      total_mrp_price: quantity * unitMRP,
    };
  };

  const calculateGrandTotals = () => {
    const totals = form.product_items.reduce(
      (acc, item) => {
        const calculated = calculateProductTotals(item);
        return {
          totalQty: acc.totalQty + calculated.quantity,
          totalDistPrice:
            acc.totalDistPrice + calculated.total_distributor_price,
          totalMRPPrice: acc.totalMRPPrice + calculated.total_mrp_price,
          totalItems: acc.totalItems + 1,
        };
      },
      { totalQty: 0, totalDistPrice: 0, totalMRPPrice: 0, totalItems: 0 },
    );
    return totals;
  };

  // ================= PRODUCT HANDLERS =================
  const handleProductSelect = (event, value) => {
    setSelectedProduct(value);
    if (value) {
      setProductForm({
        product_id: value.product_id,
        product_name: value.product_name,
        quantity: 1,
        unit_distributor_price: value.unit_distributor_price || 0,
        total_distributor_price: value.total_distributor_price || 0,
        mrp: value.mrp || 0,
      });
    } else {
      setProductForm({
        product_id: "",
        product_name: "",
        quantity: 1,
        unit_distributor_price: 0,
        total_distributor_price: 0,
        mrp: 0,
      });
    }
  };

  const handleDistributorSelect = (event, value) => {
    setSelectedDistributor(value);
    if (value) {
      setForm({ ...form, distributor_id: value.id });
      setErrors({ ...errors, distributor_id: "" });
    } else {
      setForm({ ...form, distributor_id: "" });
    }
  };

  const handleAddProduct = () => {
    if (!productForm.product_id || !productForm.product_name) {
      CommonToast("Please select a product", "error");
      return;
    }
    if (productForm.quantity <= 0) {
      CommonToast("Quantity must be greater than 0", "error");
      return;
    }
    const newProduct = calculateProductTotals(productForm);
    setForm({ ...form, product_items: [...form.product_items, newProduct] });
    setSelectedProduct(null);
    setProductForm({
      product_id: "",
      product_name: "",
      quantity: 1,
      unit_distributor_price: 0,
      total_distributor_price: 0,
      mrp: 0,
    });
    CommonToast("Product added successfully", "success");
  };

  const handleRemoveProduct = (index) => {
    const updated = form.product_items.filter((_, i) => i !== index);
    setForm({ ...form, product_items: updated });
    CommonToast("Product removed", "info");
  };

  const handleUpdateProductItem = (index, field, value) => {
    const updated = form.product_items.map((item, i) => {
      if (i === index) {
        const updatedItem = { ...item, [field]: value };
        return calculateProductTotals(updatedItem);
      }
      return item;
    });
    setForm({ ...form, product_items: updated });
  };

  const PO_STATUS_OPTIONS = [
    "DRAFT",
    "SUBMITTED",
    "APPROVED",
    "REJECTED",
    "CANCELLED",
    "PICKED",
    "PACKED",
    "DELIVERED",
  ];

  const getAvailableStatusOptions = (currentStatus) => {
    switch (currentStatus) {
      case "APPROVED":
        return ["APPROVED", "PICKED", "PACKED", "DELIVERED"];
      case "PARTIALLY_APPROVED":
        return ["PARTIALLY_APPROVED", "PICKED", "PACKED", "DELIVERED"];
      case "PICKED":
        return ["PICKED", "PACKED", "DELIVERED"];
      case "PACKED":
        return ["PACKED", "DELIVERED"];
      case "DELIVERED":
        return ["DELIVERED"];
      default:
        return [
          "DRAFT",
          "SUBMITTED",
          "APPROVED",
          "REJECTED",
          "PARTIALLY_APPROVED",
          "CANCELLED",
        ];
    }
  };

  const getStatusColor = (status) => {
    return STATUS_PALETTE[status]?.bg || "#94a3b8";
  };

  const handleStatusChange = (id, value) => {
    dispatch(
      updatePurchaseOrder({ id, data: { status: value, approved_by: 1 } }),
    );
  };

  // ================= FORM HELPERS =================
  const validate = () => {
    const temp = {};
    if (!form.po_number) temp.po_number = "PO number is required";
    if (!form.distributor_id) temp.distributor_id = "Distributor is required";
    if (!form.product_items.length)
      temp.product_items = "At least one product required";
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      CommonToast("Please fill all required fields", "error");
      return;
    }
    const submitData = {
      po_number: form.po_number,
      distributor_id: Number(form.distributor_id),
      company_id: form.company_id || null,
      product_items: form.product_items.map((item) => ({
        product_id: Number(item.product_id),
        product_name: item.product_name,
        quantity: Number(item.quantity),
        mrp: Number(item.mrp),
        unit_distributor_price: Number(item.unit_distributor_price),
        total_distributor_price:
          Number(item.quantity) * Number(item.unit_distributor_price),
        total_mrp_price: Number(item.quantity) * Number(item.mrp),
        short_pick: Number(item.short_pick),
        hsn_code: Number(item.hsn_code),
        cartons: Number(item.cartons),
        qty_picked: Number(item.qty_picked),
        gross_weight: Number(item.gross_weight),
        net_weight: Number(item.net_weight),
        remarks: item.remarks || "",
      })),
      remarks: form.remarks || "",
    };

    if (isEditing && editId) {
      dispatch(updatePurchaseOrder({ id: editId, data: submitData }))
        .unwrap()
        .then(() =>
          CommonToast("Purchase order updated successfully", "success"),
        )
        .catch((error) => {
          console.error("Update Error:", error);
          CommonToast(
            error?.message || "Failed to update purchase order",
            "error",
          );
        });
    } else {
      dispatch(createPurchaseOrder(submitData))
        .unwrap()
        .then(() =>
          CommonToast("Purchase order created successfully", "success"),
        )
        .catch((error) => {
          console.error("Create Error:", error);
          CommonToast(
            error?.message || "Failed to create purchase order",
            "error",
          );
        });
    }
  };

  const handleView = (po) => {
    setViewPO(po);
    setIsViewing(true);
    dispatch(getPOPayments(po.id)); // fetch payments alongside

    // setIsViewingPayments(true);
    handleViewPayments(po);
  };

  const handleEdit = (po) => {
    setPoStatus(po?.status);
    setIsEditing(true);
    setEditId(po.id);
    dispatch(getPOPayments(po.id));
    // setIsViewingPayments(true);
    handleViewPayments(po);
    setForm({
      po_number: po.po_number,
      distributor_id: po.distributor_id,
      product_items: po.product_items || [],
      remarks: po.remarks || "",
    });
    const dist = distributors.find((d) => d.id === po.distributor_id);
    setSelectedDistributor(dist || null);

    const comp = companies.find((c) => c.id === po.company_id);
    setSelectedCompany(comp || null);
    setForm((prev) => ({ ...prev, company_id: po.company_id || "" }));
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this PO?")) {
      dispatch(deletePurchaseOrder(id))
        .unwrap()
        .then(() =>
          CommonToast("Purchase order deleted successfully", "success"),
        )
        .catch(() => CommonToast("Failed to delete purchase order", "error"));
    }
  };

  const handleReset = () => {
    setIsEditing(false);
    setEditId(null);
    setIsViewing(false);
    setViewPO(null);
    // setIsViewingPayments(false);
    // setViewPaymentPO(null);
    setForm({
      po_number: "",
      distributor_id: "",
      product_items: [],
      remarks: "",
    });
    setProductForm({
      product_id: "",
      product_name: "",
      quantity: 1,
      unit_distributor_price: 0,
      total_distributor_price: 0,
      mrp: 0,
    });
    setSelectedProduct(null);
    setSelectedDistributor(null);
    setSelectedCompany(null);
    setErrors({});
  };

  const paginatedData = purchaseOrders?.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );
  const grandTotals = calculateGrandTotals();

  // ================= CREATE / EDIT VIEW =================
  if (isEditing) {
    return (
      <Box mt={3}>
        {/* ── Page header ── */}
        <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
          <IconButton
            onClick={handleReset}
            sx={{
              bgcolor: "#f1f5f9",
              border: "1px solid #e2e8f0",
              "&:hover": { bgcolor: "#e2e8f0" },
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
              color="#1e293b"
              lineHeight={1}
            >
              {editId ? "Edit Purchase Order" : "Create Purchase Order"}
            </Typography>
            {/* {editId && (
              <Typography variant="caption" color="text.secondary">
                ID: {editId}
              </Typography>
            )} */}
          </Box>
          {poStatus && <StatusBadge status={poStatus} />}
        </Stack>

        <Grid container spacing={3}>
          {/* ── LEFT: PO Details ── */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ ...sectionPaper, p: 0 }}>
              {/* Card header */}
              <Box
                sx={{
                  background: HEADER_BG,
                  px: 3,
                  py: 2,
                  borderRadius: "12px 12px 0 0",
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} color="white">
                  PO Details
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.7)" }}
                >
                  Fill in the order information
                </Typography>
              </Box>

              <Box sx={{ p: 3 }}>
                <Stack spacing={2.5}>
                  <TextField
                    label="PO Number"
                    value={form.po_number}
                    onChange={(e) =>
                      setForm({ ...form, po_number: e.target.value })
                    }
                    error={!!errors.po_number}
                    helperText={errors.po_number}
                    fullWidth
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                  <Autocomplete
                    options={companies}
                    getOptionLabel={(option) => option.company_name}
                    value={selectedCompany}
                    onChange={(event, value) => {
                      setSelectedCompany(value);
                      setForm({ ...form, company_id: value ? value.id : "" });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Company"
                        size="small"
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Box sx={{ py: 0.5 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {option.company_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.email}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    fullWidth
                  />
                  <Autocomplete
                    options={distributors}
                    getOptionLabel={(option) => `${option.distributor_name}`}
                    value={selectedDistributor}
                    onChange={handleDistributorSelect}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Distributor"
                        error={!!errors.distributor_id}
                        helperText={errors.distributor_id}
                        size="small"
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.distributor_id}>
                        <Box sx={{ py: 0.5 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {option.distributor_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {option.distributor_id}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option.distributor_id === value.distributor_id
                    }
                    fullWidth
                  />

                  <TextField
                    label="Remarks"
                    value={form.remarks}
                    onChange={(e) =>
                      setForm({ ...form, remarks: e.target.value })
                    }
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />

                  {/* Order Summary card */}
                  <Box
                    sx={{
                      borderRadius: 2.5,
                      border: "1.5px solid #2563eb22",
                      bgcolor: "#f0f7ff",
                      overflow: "hidden",
                    }}
                  >
                    <Box sx={{ bgcolor: "#2563eb", px: 2, py: 1 }}>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="white"
                        letterSpacing="0.05em"
                      >
                        ORDER SUMMARY
                      </Typography>
                    </Box>
                    <Stack spacing={1} sx={{ p: 2 }}>
                      {[
                        { label: "Total Items", value: grandTotals.totalItems },
                        {
                          label: "Total Quantity",
                          value: grandTotals.totalQty,
                        },
                      ].map((r) => (
                        <Stack
                          key={r.label}
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="body2" color="#64748b">
                            {r.label}
                          </Typography>
                          <Chip
                            label={r.value}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: "#dbeafe",
                              color: "#1e40af",
                              height: 22,
                            }}
                          />
                        </Stack>
                      ))}
                      <Divider sx={{ my: 0.5 }} />
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="#64748b">
                          Distributor Total
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="#1d4ed8"
                        >
                          ₹ {grandTotals.totalDistPrice.toFixed(2)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="#64748b">
                          MRP Total
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="#1e293b"
                        >
                          ₹ {grandTotals.totalMRPPrice.toFixed(2)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>

                  {errors.product_items && (
                    <Alert
                      severity="error"
                      sx={{ borderRadius: 2, fontSize: "0.82rem" }}
                    >
                      {errors.product_items}
                    </Alert>
                  )}

                  <Stack direction="row" spacing={1.5} pt={1}>
                    <Button
                      variant="outlined"
                      onClick={handleReset}
                      fullWidth
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={createLoading || updateLoading}
                      fullWidth
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 700,
                        background: HEADER_BG,
                        boxShadow: "0 4px 14px 0 rgba(37,99,235,0.35)",
                      }}
                    >
                      {createLoading || updateLoading ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={14} sx={{ color: "white" }} />
                          <span>Saving…</span>
                        </Stack>
                      ) : (
                        "Save PO"
                      )}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Paper>
          </Grid>

          {/* ── RIGHT: Products ── */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ ...sectionPaper, p: 0 }}>
              {/* Card header */}
              <Box
                sx={{
                  background: HEADER_BG,
                  px: 3,
                  py: 2,
                  borderRadius: "12px 12px 0 0",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <ShoppingCartIcon sx={{ color: "white", fontSize: 20 }} />
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    color="white"
                  >
                    Product Items
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.7)" }}
                  >
                    {form.product_items.length} product
                    {form.product_items.length !== 1 ? "s" : ""} added
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ p: 3 }}>
                {/* ── Add Product Form ── */}
                {/* <Box
                  sx={{
                    bgcolor: "#f8fafc",
                    border: "1.5px dashed #cbd5e1",
                    borderRadius: 2.5,
                    p: 2.5,
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="#64748b"
                    letterSpacing="0.06em"
                    display="block"
                    mb={1.5}
                  >
                    ADD PRODUCT
                  </Typography>
                  <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={4}>
                      <Autocomplete
                        options={products}
                        getOptionLabel={(option) => option.product_name}
                        value={selectedProduct}
                        onChange={handleProductSelect}
                        size="small"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Product"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                bgcolor: "white",
                              },
                            }}
                          />
                        )}
                        isOptionEqualToValue={(option, value) =>
                          option.product_id === value.product_id
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <TextField
                        label="Qty"
                        value={productForm.quantity}
                        onChange={(e) =>
                          setProductForm({
                            ...productForm,
                            quantity: Number(e.target.value),
                          })
                        }
                        size="small"
                        type="number"
                        inputProps={{ min: 1 }}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: "white",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <TextField
                        label="Dist. Price"
                        value={productForm.unit_distributor_price}
                        onChange={(e) =>
                          setProductForm({
                            ...productForm,
                            unit_distributor_price: Number(e.target.value),
                          })
                        }
                        size="small"
                        type="number"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">₹</InputAdornment>
                          ),
                        }}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: "white",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <TextField
                        label="MRP"
                        value={productForm.mrp}
                        disabled
                        size="small"
                        type="number"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">₹</InputAdornment>
                          ),
                        }}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: "#f1f5f9",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddProduct}
                        fullWidth
                        sx={{
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 700,
                          height: 40,
                          background: "linear-gradient(135deg,#10b981,#059669)",
                          boxShadow: "0 4px 12px rgba(16,185,129,0.3)",
                        }}
                      >
                        Add
                      </Button>
                    </Grid>
                  </Grid>
                </Box> */}

                {/* ── Product Table ── */}
                {form.product_items.length === 0 ? (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 6,
                      px: 3,
                      bgcolor: "#f8fafc",
                      borderRadius: 2,
                      border: "1px dashed #cbd5e1",
                    }}
                  >
                    <ShoppingCartIcon
                      sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }}
                    />
                    <Typography color="text.secondary" fontSize="0.9rem">
                      No products added yet. Use the form above to add products.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer
                    sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}
                  >
                    <Table size="small">
                      <TableHead sx={tableHeadSx}>
                        <TableRow>
                          {[
                            "Sr",
                            "Product",
                            "Available Stock",
                            "Qty",
                            "Dist. Price",
                            "Total Dist. Price",
                            "MRP",
                            "Total Dist.",
                            // "Total MRP",
                          ].map((h) => (
                            <TableCell
                              key={h}
                              align={
                                ["Sr", "Product"].includes(h)
                                  ? "left"
                                  : "center"
                              }
                            >
                              {h}
                            </TableCell>
                          ))}
                          {(poStatus === "PICKED" ||
                            poStatus === "PACKED" ||
                            poStatus === "APPROVED" ||
                            poStatus === "DELIVERED") && (
                            <>
                              <TableCell align="center">Qty Picked</TableCell>
                              <TableCell align="center">Short Pick</TableCell>
                              <TableCell align="center">Remarks</TableCell>
                            </>
                          )}
                          {(poStatus === "PICKED" ||
                            poStatus === "PACKED" ||
                            poStatus === "DELIVERED") && (
                            <>
                              <TableCell align="center">HSN Code</TableCell>
                              <TableCell align="center">
                                Cartons/Boxes
                              </TableCell>
                              <TableCell align="center">
                                Gross Wt (KG)
                              </TableCell>
                              <TableCell align="center">Net Wt (KG)</TableCell>
                            </>
                          )}
                          <TableCell align="center">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {form.product_items.map((item, index) => {
                          const calculated = calculateProductTotals(item);
                          return (
                            <TableRow key={index} sx={tableRowHoverSx}>
                              <TableCell>
                                <Chip
                                  label={index + 1}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.7rem",
                                    bgcolor: "#f1f5f9",
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  color="#1e293b"
                                >
                                  {item.product_name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  ID: {item.product_id}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <TextField
                                  disabled={true}
                                  value={item.stock_available}
                                  onChange={(e) =>
                                    handleUpdateProductItem(
                                      index,
                                      "stock_available",
                                      Number(e.target.value),
                                    )
                                  }
                                  size="small"
                                  type="number"
                                  inputProps={{
                                    min: 1,
                                    style: { textAlign: "center" },
                                  }}
                                  sx={{
                                    width: 70,
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 1.5,
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <TextField
                                  value={item.quantity}
                                  onChange={(e) =>
                                    handleUpdateProductItem(
                                      index,
                                      "quantity",
                                      Number(e.target.value),
                                    )
                                  }
                                  error={item.quantity > item.stock_available}
                                  helperText={
                                    item.quantity > item.stock_available
                                      ? `Exceeds stock (${item.stock_available})`
                                      : ""
                                  }
                                  size="small"
                                  type="number"
                                  inputProps={{
                                    min: 1,
                                    style: { textAlign: "center" },
                                  }}
                                  sx={{
                                    width: 70,
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 1.5,
                                    },
                                  }}
                                />
                              </TableCell>

                              <TableCell align="center">
                                <TextField
                                  value={item.unit_distributor_price}
                                  onChange={(e) =>
                                    handleUpdateProductItem(
                                      index,
                                      "unit_distributor_price",
                                      Number(e.target.value),
                                    )
                                  }
                                  size="small"
                                  type="number"
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        ₹
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{
                                    width: 100,
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 1.5,
                                    },
                                  }}
                                />
                              </TableCell>

                              <TableCell align="center">
                                <TextField
                                  value={item.total_distributor_price || 0}
                                  onChange={(e) =>
                                    handleUpdateProductItem(
                                      index,
                                      "total_distributor_price",
                                      Number(e.target.value),
                                    )
                                  }
                                  size="small"
                                  type="number"
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        ₹
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{
                                    width: 100,
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 1.5,
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <TextField
                                  disabled
                                  value={item.mrp}
                                  onChange={(e) =>
                                    handleUpdateProductItem(
                                      index,
                                      "mrp",
                                      Number(e.target.value),
                                    )
                                  }
                                  size="small"
                                  type="number"
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        ₹
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{
                                    width: 100,
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 1.5,
                                      bgcolor: "#f8fafc",
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  color="#1d4ed8"
                                >
                                  ₹{" "}
                                  {calculated.total_distributor_price.toFixed(
                                    2,
                                  )}
                                </Typography>
                              </TableCell>
                              {/* <TableCell align="center">
                                <Typography variant="body2" color="#475569">
                                  ₹ {calculated.total_mrp_price.toFixed(2)}
                                </Typography>
                              </TableCell> */}

                              {(poStatus === "PICKED" ||
                                poStatus === "APPROVED" ||
                                poStatus === "DELIVERED" ||
                                poStatus === "PACKED") && (
                                <>
                                  <TableCell align="center">
                                    <TextField
                                      disabled={poStatus !== "APPROVED"}
                                      value={item.qty_picked || ""}
                                      onChange={(e) =>
                                        handleUpdateProductItem(
                                          index,
                                          "qty_picked",
                                          Number(e.target.value),
                                        )
                                      }
                                      size="small"
                                      type="number"
                                      inputProps={{
                                        min: 1,
                                        style: { textAlign: "center" },
                                      }}
                                      sx={{
                                        width: 70,
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: 1.5,
                                        },
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <TextField
                                      disabled={poStatus !== "APPROVED"}
                                      value={item.short_pick || ""}
                                      onChange={(e) =>
                                        handleUpdateProductItem(
                                          index,
                                          "short_pick",
                                          Number(e.target.value),
                                        )
                                      }
                                      size="small"
                                      type="number"
                                      inputProps={{
                                        min: 0,
                                        style: { textAlign: "center" },
                                      }}
                                      sx={{
                                        width: 70,
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: 1.5,
                                        },
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <TextField
                                      value={item.remarks || ""}
                                      onChange={(e) =>
                                        handleUpdateProductItem(
                                          index,
                                          "remarks",
                                          e.target.value,
                                        )
                                      }
                                      size="small"
                                      type="text"
                                      inputProps={{
                                        style: { textAlign: "center" },
                                      }}
                                      sx={{
                                        width: 120,
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: 1.5,
                                        },
                                      }}
                                    />
                                  </TableCell>
                                  {(poStatus === "PICKED" ||
                                    poStatus === "PACKED" ||
                                    poStatus === "DELIVERED") && (
                                    <>
                                      <TableCell align="center">
                                        <TextField
                                          disabled={true}
                                          value={item.hsn_code || ""}
                                          onChange={(e) =>
                                            handleUpdateProductItem(
                                              index,
                                              "hsn_code",
                                              Number(e.target.value),
                                            )
                                          }
                                          size="small"
                                          type="number"
                                          inputProps={{
                                            min: 0,
                                            style: { textAlign: "center" },
                                          }}
                                          sx={{
                                            width: 70,
                                            "& .MuiOutlinedInput-root": {
                                              borderRadius: 1.5,
                                            },
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell align="center">
                                        <TextField
                                          disabled={
                                            !(
                                              poStatus === "PACKED" ||
                                              poStatus === "PICKED"
                                            )
                                          }
                                          // disabled={(poStatus !== "PACKED")}
                                          value={item.cartons || ""}
                                          onChange={(e) =>
                                            handleUpdateProductItem(
                                              index,
                                              "cartons",
                                              Number(e.target.value),
                                            )
                                          }
                                          size="small"
                                          type="number"
                                          inputProps={{
                                            min: 0,
                                            style: { textAlign: "center" },
                                          }}
                                          sx={{
                                            width: 70,
                                            "& .MuiOutlinedInput-root": {
                                              borderRadius: 1.5,
                                            },
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell align="center">
                                        <TextField
                                          // disabled={poStatus !== "PACKED"}                                        disabled={!(poStatus === "PACKED" || poStatus === "PICKED")}
                                          disabled={
                                            !(
                                              poStatus === "PACKED" ||
                                              poStatus === "PICKED"
                                            )
                                          }
                                          value={item.gross_weight || ""}
                                          onChange={(e) =>
                                            handleUpdateProductItem(
                                              index,
                                              "gross_weight",
                                              Number(e.target.value),
                                            )
                                          }
                                          size="small"
                                          type="number"
                                          inputProps={{
                                            min: 0,
                                            style: { textAlign: "center" },
                                          }}
                                          sx={{
                                            width: 70,
                                            "& .MuiOutlinedInput-root": {
                                              borderRadius: 1.5,
                                            },
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell align="center">
                                        <TextField
                                          // disabled={poStatus !== "PACKED"}
                                          disabled={
                                            !(
                                              poStatus === "PACKED" ||
                                              poStatus === "PICKED"
                                            )
                                          }
                                          value={item.net_weight || ""}
                                          onChange={(e) =>
                                            handleUpdateProductItem(
                                              index,
                                              "net_weight",
                                              Number(e.target.value),
                                            )
                                          }
                                          size="small"
                                          type="number"
                                          inputProps={{
                                            min: 0,
                                            style: { textAlign: "center" },
                                          }}
                                          sx={{
                                            width: 70,
                                            "& .MuiOutlinedInput-root": {
                                              borderRadius: 1.5,
                                            },
                                          }}
                                        />
                                      </TableCell>
                                      {/* <TableCell align="center">
                                        <TextField
                                          value={item.remarks || ""}
                                          onChange={(e) =>
                                            handleUpdateProductItem(
                                              index,
                                              "remarks",
                                              e.target.value,
                                            )
                                          }
                                          size="small"
                                          type="text"
                                          inputProps={{
                                            style: { textAlign: "center" },
                                          }}
                                          sx={{
                                            width: 120,
                                            "& .MuiOutlinedInput-root": {
                                              borderRadius: 1.5,
                                            },
                                          }}
                                        />
                                      </TableCell> */}
                                    </>
                                  )}
                                </>
                              )}

                              <TableCell align="center">
                                <Tooltip title="Remove">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveProduct(index)}
                                    sx={{
                                      color: "#ef4444",
                                      "&:hover": { bgcolor: "#fef2f2" },
                                    }}
                                  >
                                    <RemoveCircleOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
        <PaymentsPanel
          poPayments={poPayments}
          paymentsLoading={paymentsLoading}
          BASE_URL={BASE_URL}
          dispatch={dispatch}
          getPOPayments={getPOPayments}
          createPOPayment={createPOPayment}
          updatePaymentStatus={updatePaymentStatus}
        />
      </Box>
    );
  }

  // ================= VIEW MODE =================
  if (isViewing && viewPO) {
    const viewTotals = viewPO.product_items?.reduce(
      (acc, item) => ({
        totalDistPrice:
          acc.totalDistPrice + (item.total_distributor_price || 0),
        totalMRPPrice: acc.totalMRPPrice + (item.total_mrp_price || 0),
      }),
      { totalDistPrice: 0, totalMRPPrice: 0 },
    );

    const distributorInfo = distributors.find(
      (d) => d.id === viewPO.distributor_id,
    );
    const companyInfo = companies.find((c) => c.id === viewPO.company_id);

    return (
      <Box mt={3}>
        {/* ── Page header ── */}
        <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
          <IconButton
            onClick={handleReset}
            sx={{
              bgcolor: "#f1f5f9",
              border: "1px solid #e2e8f0",
              "&:hover": { bgcolor: "#e2e8f0" },
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={700} color="#1e293b">
              View Purchase Order
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {viewPO.po_number}
            </Typography>
          </Box>
          <StatusBadge status={viewPO.status} />
        </Stack>

        <Grid container spacing={3}>
          {/* ── LEFT: Order Details ── */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ ...sectionPaper, p: 0 }}>
              <Box
                sx={{
                  background: HEADER_BG,
                  px: 3,
                  py: 2,
                  borderRadius: "12px 12px 0 0",
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} color="white">
                  Order Details
                </Typography>
              </Box>

              <Box sx={{ p: 3 }}>
                <Stack spacing={2.5}>
                  {/* PO Number */}
                  <Box sx={{ bgcolor: "#f8fafc", borderRadius: 2, p: 1.5 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      letterSpacing="0.06em"
                    >
                      PO NUMBER
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      color="#1e293b"
                      mt={0.3}
                    >
                      {viewPO.po_number}
                    </Typography>
                  </Box>

                  <Box sx={{ bgcolor: "#f8fafc", borderRadius: 2, p: 1.5 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      letterSpacing="0.06em"
                    >
                      COMPANY
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      color="#1e293b"
                      mt={0.3}
                    >
                      {companyInfo?.company_name || "N/A"}
                    </Typography>
                  </Box>

                  {/* Distributor */}
                  <Box sx={{ bgcolor: "#f8fafc", borderRadius: 2, p: 1.5 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      letterSpacing="0.06em"
                    >
                      DISTRIBUTOR
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      color="#1e293b"
                      mt={0.3}
                    >
                      {distributorInfo?.distributor_name || "N/A"}
                    </Typography>
                  </Box>

                  {/* PO Date */}
                  <Box sx={{ bgcolor: "#f8fafc", borderRadius: 2, p: 1.5 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      letterSpacing="0.06em"
                    >
                      PO DATE
                    </Typography>
                    <Typography variant="body2" color="#1e293b" mt={0.3}>
                      {new Date(viewPO.po_date).toLocaleString()}
                    </Typography>
                  </Box>

                  {/* Remarks */}
                  {viewPO.remarks && (
                    <Box sx={{ bgcolor: "#f8fafc", borderRadius: 2, p: 1.5 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                        letterSpacing="0.06em"
                      >
                        REMARKS
                      </Typography>
                      <Typography variant="body2" color="#475569" mt={0.3}>
                        {viewPO.remarks}
                      </Typography>
                    </Box>
                  )}

                  {/* Summary */}
                  <Box
                    sx={{
                      borderRadius: 2.5,
                      border: "1.5px solid #10b98122",
                      bgcolor: "#f0fdf4",
                      overflow: "hidden",
                    }}
                  >
                    <Box sx={{ bgcolor: "#10b981", px: 2, py: 1 }}>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="white"
                        letterSpacing="0.05em"
                      >
                        ORDER SUMMARY
                      </Typography>
                    </Box>
                    <Stack spacing={1} sx={{ p: 2 }}>
                      {[
                        {
                          label: "Total Items",
                          value: viewPO.total_items || 0,
                        },
                        {
                          label: "Total Quantity",
                          value: viewPO.total_quantity || 0,
                        },
                      ].map((r) => (
                        <Stack
                          key={r.label}
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="body2" color="#64748b">
                            {r.label}
                          </Typography>
                          <Chip
                            label={r.value}
                            size="small"
                            sx={{
                              height: 22,
                              fontWeight: 700,
                              bgcolor: "#dcfce7",
                              color: "#166534",
                            }}
                          />
                        </Stack>
                      ))}
                      <Divider sx={{ my: 0.5 }} />
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="#64748b">
                          Distributor Total
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="#065f46"
                        >
                          ₹ {viewTotals?.totalDistPrice.toFixed(2) || "0.00"}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="#64748b">
                          MRP Total
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="#1e293b"
                        >
                          ₹ {viewTotals?.totalMRPPrice.toFixed(2) || "0.00"}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            </Paper>
          </Grid>

          {/* ── RIGHT: Product Items ── */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ ...sectionPaper, p: 0 }}>
              <Box
                sx={{
                  background: HEADER_BG,
                  px: 3,
                  py: 2,
                  borderRadius: "12px 12px 0 0",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <ShoppingCartIcon sx={{ color: "white", fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight={700} color="white">
                  Product Items
                </Typography>
                <Chip
                  label={viewPO.product_items?.length || 0}
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    color: "white",
                    fontWeight: 700,
                    height: 22,
                  }}
                />
              </Box>

              <Box sx={{ p: 3 }}>
                <TableContainer
                  sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}
                >
                  <Table size="small">
                    <TableHead sx={tableHeadSx}>
                      <TableRow>
                        {[
                          "#",
                          "Product",
                          "Quantity",
                          "Dist. Price",
                          "MRP",
                          "Total Dist.",
                          "Total Dist. Price",
                          // "Total MRP",
                        ].map((h) => (
                          <TableCell
                            key={h}
                            align={
                              ["#", "Product"].includes(h) ? "left" : "center"
                            }
                          >
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {viewPO.product_items?.map((item, index) => (
                        <TableRow key={index} sx={tableRowHoverSx}>
                          <TableCell>
                            <Chip
                              label={index + 1}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.7rem",
                                bgcolor: "#f1f5f9",
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color="#1e293b"
                            >
                              {item?.product_name}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={item?.quantity}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                bgcolor: "#eff6ff",
                                color: "#1e40af",
                                height: 22,
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" color="#475569">
                              ₹ {item?.unit_distributor_price}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" color="#475569">
                              ₹ {item?.total_distributor_price}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" color="#475569">
                              ₹ {item?.mrp}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              color="#1d4ed8"
                            >
                              ₹ {item?.total_distributor_price?.toFixed(2)}
                            </Typography>
                          </TableCell>
                          {/* <TableCell align="center">
                            <Typography variant="body2" color="#475569">
                              ₹ {item?.total_mrp_price?.toFixed(2)}
                            </Typography>
                          </TableCell> */}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
        <PaymentsPanel
          poPayments={poPayments}
          paymentsLoading={paymentsLoading}
          BASE_URL={BASE_URL}
          dispatch={dispatch}
          getPOPayments={getPOPayments}
          createPOPayment={createPOPayment}
          updatePaymentStatus={updatePaymentStatus}
        />
      </Box>
    );
  }

  // ================= PAYMENTS VIEW =================

  // ================= PAYMENTS VIEW =================

  // ================= LIST VIEW =================

  return (
    <Box>
      {/* ── Page Header ── */}
      {/* <Box
        sx={{
          background: HEADER_BG,
          borderRadius: 3,
        px: 3.5,
          py: 2.5,
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 4px 20px rgba(37,99,235,0.25)",
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800} color="white">
            Purchase Orders
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
            Manage and track all purchase orders
          </Typography>
        </Box>
        <Chip
          label={purchaseOrders?.length || 0}
          sx={{
            bgcolor: "rgba(255,255,255,0.2)",
            color: "white",
            fontWeight: 700,
            fontSize: "1rem",
            height: 36,
            px: 1,
          }}
        />
      </Box> */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{ width: 5, height: 32, bgcolor: "#D20000", borderRadius: 1 }}
          />
          <Typography variant="h5" fontWeight={800} color="#1a1a1a">
            Purchase Order
          </Typography>
        </Box>
      </Stack>
      {/* ── Table Card ── */}
      <Paper sx={{ ...sectionPaper }}>
        {/* Search bar */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: "1px solid #f1f5f9",
            bgcolor: "#fafbfc",
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
            <SearchIcon sx={{ color: "#D20000", fontSize: 18 }} />
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              letterSpacing={0.5}
            >
              FILTER P/O
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <CommonSearchField
              value={searchQuery}
              placeholder="Search PO number…"
              onChange={(value) => setSearchQuery(value)}
            />

            <CommonSelectField
              value={statusFilter}
              onChange={(v) => setStatusFilter(v)}
              options={Object.keys(STATUS_PALETTE).map((s) => ({
                value: s,
                label: s,
              }))}
              placeholder="Filter by Status"
              sx={{ width: "250px", ml: 1 }}
            />
          </Box>
          <CommonSelectField
            value={distributorFilter}
            onChange={(v) => setDistributorFilter(v)}
            options={
              distributors?.map((d) => ({
                value: d.id ?? d.distributor_id,
                label: d.distributor_name || d.name || d.distributor_name,
              })) || []
            }
            placeholder="Filter by Distributor"
            sx={{ width: "200px", ml: 1, mt: 1.5 }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  background:
                    "linear-gradient(90deg, #D20000 0%, #8B0000 100%)",
                }}
              >
                {[
                  "Sr",
                  "PO Number",
                  "Distributor",
                  "PO Date",
                  "Status",
                  "Total Items",
                  "Total Qty",
                  "Total Dist. Price",
                  "Paid Amount",
                  "Actions",
                  "Picked PDF",
                  "Packed PDF",
                  "Shipment",
                  "Invoice",
                  "Barcode Sticker",
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      fontWeight: 700,
                      color: "#fff",
                      fontSize: 13,
                      letterSpacing: 0.5,
                      border: "none",
                      py: 1.5,
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 6 }}>
                    <Stack alignItems="center" spacing={1.5}>
                      <CircularProgress size={32} thickness={4} />
                      <Typography variant="body2" color="text.secondary">
                        Loading purchase orders…
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                paginatedData?.map((po, index) => {
                  const distributorInfo = distributors.find(
                    (d) => d.id === po.distributor_id,
                  );
                  const p = STATUS_PALETTE[po.status] || STATUS_PALETTE.DRAFT;
                  return (
                    <TableRow key={po.id} sx={tableRowHoverSx}>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={600}
                        >
                          {(page - 1) * rowsPerPage + index + 1}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="#1d4ed8"
                          sx={{ letterSpacing: "0.02em" }}
                        >
                          {po.po_number}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="#1e293b"
                        >
                          {po?.distributor_name || "N/A"}
                        </Typography>
                        {/* <Typography variant="caption" color="text.secondary">
                          ID: {po?.distributor_id}
                        </Typography> */}
                      </TableCell>

                      <TableCell align="center">
                        <Typography variant="body2" color="#475569">
                          {new Date(po?.po_date).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Select
                          size="small"
                          value={po?.status}
                          onChange={(e) =>
                            handleStatusChange(po.id, e.target.value)
                          }
                          disabled={po?.status === "DELIVERED"}
                          sx={{
                            minWidth: 148,
                            height: 28,
                            borderRadius: "999px",
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            letterSpacing: "0.04em",
                            color: "white",
                            bgcolor: p.bg,
                            "& .MuiSelect-select": {
                              py: 0.5,
                              pl: 2,
                              display: "flex",
                              alignItems: "center",
                            },
                            "& fieldset": { border: "none" },
                            "& svg": {
                              color:
                                po?.status === "DELIVERED"
                                  ? "transparent"
                                  : "rgba(255,255,255,0.8)",
                            },
                            boxShadow: `0 2px 8px ${p.bg}55`,
                          }}
                        >
                          {getAvailableStatusOptions(po?.status).map(
                            (option) => (
                              <MenuItem
                                key={option}
                                value={option}
                                sx={{ fontSize: "0.82rem", fontWeight: 600 }}
                              >
                                {option}
                              </MenuItem>
                            ),
                          )}
                        </Select>
                      </TableCell>

                      <TableCell align="center">
                        <Chip
                          label={po.total_items || 0}
                          size="small"
                          sx={{
                            bgcolor: "#f1f5f9",
                            fontWeight: 700,
                            height: 22,
                          }}
                        />
                      </TableCell>

                      <TableCell align="center">
                        <Chip
                          label={po.total_quantity}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 700,
                            borderColor: "#cbd5e1",
                            height: 22,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={po.distributor_price}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 700,
                            borderColor: "#cbd5e1",
                            height: 22,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={po.paid_amount || 0}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 700,
                            borderColor: "#cbd5e1",
                            height: 22,
                          }}
                        />
                      </TableCell>

                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="center"
                        >
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              onClick={() => handleView(po)}
                              sx={{
                                bgcolor: "#eff6ff",
                                color: "#2563eb",
                                "&:hover": { bgcolor: "#dbeafe" },
                              }}
                            >
                              <VisibilityIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(po)}
                              sx={{
                                bgcolor: "#fefce8",
                                color: "#ca8a04",
                                "&:hover": { bgcolor: "#fef9c3" },
                              }}
                            >
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          {/* <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              disabled={deleteLoading}
                              onClick={() => handleDelete(po.id)}
                              sx={{
                                bgcolor: "#fef2f2",
                                "&:hover": { bgcolor: "#fee2e2" },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip> */}
                        </Stack>
                      </TableCell>

                      {/* Picked PDF */}
                      <TableCell align="center">
                        {(po?.status === "PICKED" ||
                          po?.status === "APPROVED") && (
                          <Tooltip title="Download Picked PDF">
                            <IconButton
                              size="small"
                              sx={{
                                bgcolor: "#fff7ed",
                                color: "#f97316",
                                "&:hover": { bgcolor: "#fed7aa" },
                              }}
                              onClick={async () => {
                                const response = await fetch(
                                  `https://apidata.hogonnindia.com/purchase-orders/${po?.id}/picked-pdf/`,
                                );
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = `PO-${editId}.pdf`;
                                a.click();
                                window.URL.revokeObjectURL(url);
                              }}
                            >
                              <DownloadIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>

                      {/* Packed PDF */}
                      <TableCell align="center">
                        {(po?.status === "PICKED" ||
                          po?.status === "PACKED") && (
                          <Tooltip title="Download Packed PDF">
                            <IconButton
                              size="small"
                              sx={{
                                bgcolor: "#eff6ff",
                                color: "#3b82f6",
                                "&:hover": { bgcolor: "#dbeafe" },
                              }}
                              onClick={async () => {
                                const response = await fetch(
                                  `https://apidata.hogonnindia.com/purchase-orders/${po?.id}/packing-pdf/`,
                                );
                                const blob = await response.blob();
                                ``;
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = `PO-${editId}.pdf`;
                                a.click();
                                window.URL.revokeObjectURL(url);
                              }}
                            >
                              <DownloadIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>

                      {/* Shipment Form — visible when PACKED */}
                      <TableCell align="center">
                        {po?.status === "PACKED" && (
                          <Tooltip title="Create Shipment">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenShipmentForm(po)}
                              sx={{
                                bgcolor: "#f0fdf4",
                                color: "#16a34a",
                                "&:hover": { bgcolor: "#dcfce7" },
                              }}
                            >
                              <LocalShippingIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>

                      {/* Invoice PDF — visible when DELIVERED */}
                      <TableCell align="center">
                        {po?.status === "DELIVERED" && (
                          <Tooltip title="Download Invoice">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadInvoice(po.id)}
                              sx={{
                                bgcolor: "#fdf4ff",
                                color: "#9333ea",
                                "&:hover": { bgcolor: "#f3e8ff" },
                              }}
                            >
                              <ReceiptIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>

                      {/* ── Barcode Sticker PDF ── */}
                      <TableCell align="center">
                        {["PICKED", "PACKED", "DELIVERED"].includes(
                          po?.status,
                        ) && (
                          <Tooltip title="Download Barcode Sticker">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDownloadWarrantyCard(po.po_number)
                              }
                              sx={{
                                bgcolor: "#f3e8ff",
                                color: "#7c3aed",
                                "&:hover": { bgcolor: "#ede9fe" },
                              }}
                            >
                              <ShieldIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}

              {!loading && purchaseOrders?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                    <ShoppingCartIcon
                      sx={{
                        fontSize: 40,
                        color: "#cbd5e1",
                        mb: 1,
                        display: "block",
                        mx: "auto",
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      No purchase orders found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderTop: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Pagination
            count={Math.ceil((purchaseOrders?.length || 0) / rowsPerPage)}
            page={page}
            onChange={(_, v) => setPage(v)}
            shape="rounded"
            color="primary"
            size="small"
          />
        </Box>
        {/* Shipment Form Dialog */}
        <ShipmentFormDialog
          open={shipmentDialogOpen}
          onClose={() => setShipmentDialogOpen(false)}
          poId={shipmentPOId}
          dispatch={dispatch}
          createLoading={shipmentCreateLoading}
          updateLoading={shipmentUpdateLoading}
        />
      </Paper>
    </Box>
  );
};

export default PurchaseOrder;
